import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { KBSidebar } from "@/components/KBSidebar";
import { StatoBadge } from "@/pages/KnowledgeBase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PlusIcon as Plus, CaretRightIcon as CaretRight, PencilSimpleIcon as PencilSimple, TrashIcon as Trash, ArticleIcon as Article } from "@phosphor-icons/react";

const FILTERS = [["", "Tutti"], ["pubblicato", "Pubblicati"], ["bozza", "Bozze"], ["archiviato", "Archiviati"]];

export default function KBFolder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [folder, setFolder] = useState(null);
  const [articles, setArticles] = useState([]);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("updated");
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: f }, { data: arts }] = await Promise.all([
        api.get(`/kb/folders/${id}`),
        api.get(`/kb/articles`, { params: { cartella_id: id } }),
      ]);
      setFolder(f); setArticles(arts || []);
    } catch { setFolder(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const shown = (articles || [])
    .filter((a) => !filter || a.stato === filter)
    .sort((a, b) => {
      if (sort === "titolo") return (a.titolo || "").localeCompare(b.titolo || "");
      if (sort === "stato") return (a.stato || "").localeCompare(b.stato || "");
      if (sort === "autore") return (a.autore_nome || "").localeCompare(b.autore_nome || "");
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });

  const saveFolder = async () => {
    if (!edit.nome?.trim()) { toast.error("Nome obbligatorio"); return; }
    try { await api.put(`/kb/folders/${id}`, { nome: edit.nome, descrizione: edit.descrizione }); toast.success("Cartella aggiornata"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };
  const delFolder = async () => {
    if (!window.confirm(`Eliminare la cartella "${folder.nome}" e i suoi articoli?`)) return;
    try { await api.delete(`/kb/folders/${id}`); toast.success("Cartella eliminata"); nav("/knowledge-base"); }
    catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  if (!folder) return <InternalLayout><div className="max-w-[1400px] mx-auto p-4 text-sm text-muted-foreground">Caricamento…</div></InternalLayout>;

  return (
    <InternalLayout>
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4" data-testid="kb-breadcrumb">
          <button onClick={() => nav("/knowledge-base")} className="hover:text-foreground hover:underline">Knowledge Base</button>
          <CaretRight size={12} /><span>{folder.categoria?.nome}</span>
          <CaretRight size={12} /><span className="text-foreground font-medium">{folder.nome}</span>
        </div>

        <div className="flex gap-5">
          <KBSidebar activeCategoryId={folder.categoria_id} activeFolderId={id} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h1 className="font-heading text-xl font-bold text-slate-800">{folder.nome}</h1>
                {folder.descrizione && <p className="text-sm text-muted-foreground mt-0.5">{folder.descrizione}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="rounded-sm h-9 w-9" title="Modifica cartella" onClick={() => setEdit({ nome: folder.nome, descrizione: folder.descrizione })} data-testid="kb-folder-edit"><PencilSimple size={16} /></Button>
                <Button variant="outline" size="icon" className="rounded-sm h-9 w-9 text-rose-600 border-rose-200 hover:bg-rose-50" title="Elimina cartella" onClick={delFolder} data-testid="kb-folder-delete"><Trash size={16} /></Button>
                <Button onClick={() => nav(`/knowledge-base/article/new?folder=${id}`)} className="rounded-sm gap-1.5" data-testid="kb-new-article-btn"><Plus size={16} /> Nuovo articolo</Button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-1 bg-secondary/50 rounded-sm p-0.5">
                {FILTERS.map(([v, l]) => (
                  <button key={v} onClick={() => setFilter(v)} className={`text-sm px-3 py-1 rounded-sm ${filter === v ? "bg-white shadow-sm font-medium text-slate-800" : "text-muted-foreground"}`} data-testid={`kb-filter-${v || "tutti"}`}>{l}</button>
                ))}
              </div>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-sm border border-border bg-white text-sm px-2" data-testid="kb-sort">
                <option value="updated">Ultima modifica</option>
                <option value="titolo">Titolo</option>
                <option value="stato">Stato</option>
                <option value="autore">Autore</option>
              </select>
            </div>

            <div className="space-y-2" data-testid="kb-article-list">
              {shown.map((a) => (
                <Card key={a.id} className="rounded-sm shadow-none p-3 hover:border-blue-300 cursor-pointer flex items-center gap-3" onClick={() => nav(`/knowledge-base/article/${a.id}`)} data-testid={`kb-article-row-${a.id}`}>
                  <Article size={18} className="text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-800 truncate">{a.titolo}</span>
                      <StatoBadge stato={a.stato} />
                      {a.visibile_area_cliente && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">Area cliente</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.autore_nome} · agg. {fmtDate(a.updated_at)}</p>
                  </div>
                </Card>
              ))}
              {shown.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nessun articolo in questa cartella.</p>}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Modifica cartella</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Nome *</Label><Input value={edit.nome} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} className="rounded-sm" data-testid="kb-folder-edit-name" /></div>
              <div><Label>Descrizione</Label><Textarea value={edit.descrizione} onChange={(e) => setEdit({ ...edit, descrizione: e.target.value })} rows={2} className="rounded-sm" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)} className="rounded-sm">Annulla</Button><Button onClick={saveFolder} className="rounded-sm" data-testid="kb-folder-edit-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
